import {Box, Chip, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {Colors} from 'appearance'
import Tooltip from 'components/Display/Tooltip/Tooltip'

const MAX_VISIBLE_CHANGES = 3
const MAX_VALUE_LENGTH = 30

const ACTION_COLORS = {
    CREATE: 'success',
    DELETE: 'error',
    LOGIN_FAILED: 'error',
    TWO_FACTOR_FAILED: 'error',
    PERMISSION_CHANGED: 'warning',
    ROLE_CHANGED: 'warning',
    USER_REMOVED_FROM_ACCOUNT: 'warning',
    PASSWORD_RESET: 'warning',
}

const formatValue = (value) => {
    if (value === null || value === undefined) return '∅'
    return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

const truncate = (text) =>
    text.length > MAX_VALUE_LENGTH ? `${text.slice(0, MAX_VALUE_LENGTH)}…` : text

export const AuditAction = ({action}) => {
    const {t} = useTranslation()
    return (
        <Chip
            size="small"
            variant="outlined"
            color={ACTION_COLORS[action] || 'default'}
            label={t(`selectOptions.${action}`, {defaultValue: action})}
        />
    )
}

export const AuditActor = ({actor}) => {
    const {t} = useTranslation()
    const {type, user} = actor || {}
    if (!user) return t(`selectOptions.${type}`, {defaultValue: type || '-'})
    return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
            <span>{user.name || user._id}</span>
            {user.isSuperUser && (
                <Chip size="small" label={t('audit_logs.superUser')} sx={{height: 18}} />
            )}
        </Box>
    )
}

export const AuditEntity = ({entity, meta}) => {
    const {t} = useTranslation()
    const {type, id, name} = entity || {}
    // e.g. EXPORT is about a resource, not one entity
    if (!type) return meta?.resource || '-'
    return (
        <Box display="flex" flexDirection="column" alignItems="center" lineHeight={1.25}>
            <Typography variant="caption" sx={{fontWeight: 600}}>
                {t(`selectOptions.${type}`, {defaultValue: type})}
            </Typography>
            {(name || id) && (
                <Typography variant="caption" sx={{color: Colors.gray}}>
                    {name || id}
                </Typography>
            )}
        </Box>
    )
}

// Hidden (PII) fields are stored by name only — no from/to — so they show as "changed"
const ChangeLine = ({change}) => {
    const {t} = useTranslation()
    const {field} = change
    const hasValues = 'from' in change || 'to' in change
    if (!hasValues) {
        return (
            <Typography variant="caption">
                <b>{field}</b>{' '}
                <span style={{color: Colors.gray}}>{t('audit_logs.valueHidden')}</span>
            </Typography>
        )
    }
    const from = formatValue(change.from)
    const to = formatValue(change.to)
    return (
        <Tooltip title={`${from} → ${to}`} arrow>
            <Typography variant="caption" noWrap>
                <b>{field}</b>: {truncate(from)} → {truncate(to)}
            </Typography>
        </Tooltip>
    )
}

export const AuditChanges = ({changes, meta}) => {
    if (!changes?.length) return meta?.count != null ? `× ${meta.count}` : '-'
    const hiddenCount = changes.length - MAX_VISIBLE_CHANGES
    return (
        <Box display="flex" flexDirection="column" alignItems="flex-start" maxWidth={320}>
            {changes.slice(0, MAX_VISIBLE_CHANGES).map((change) => (
                <ChangeLine key={change.field} change={change} />
            ))}
            {hiddenCount > 0 && (
                <Typography variant="caption" sx={{color: Colors.gray}}>
                    +{hiddenCount}
                </Typography>
            )}
            {meta?.count != null && (
                <Typography variant="caption" sx={{color: Colors.gray}}>
                    × {meta.count}
                </Typography>
            )}
        </Box>
    )
}
